// Talks to an embedded YouTube player through postMessage (the protocol the official IFrame API
// uses), so no YouTube script runs in our page: the CSP only has to allow the player's frame.

export const EMBED_ORIGIN = 'https://www.youtube-nocookie.com';

export const PlayerState = {
  unstarted: -1,
  ended: 0,
  playing: 1,
  paused: 2,
  buffering: 3,
  cued: 5,
} as const;

export function embedUrl(id: string): string {
  const params = new URLSearchParams({
    enablejsapi: '1',
    origin: window.location.origin,
    playsinline: '1',
    rel: '0',
  });
  return `${EMBED_ORIGIN}/embed/${id}?${params.toString()}`;
}

interface Info {
  currentTime?: number;
  duration?: number;
  playerState?: number;
  playbackRate?: number;
}

/** The embedded player's clock and state, and its commands. */
export class YouTubePlayer {
  state: number = PlayerState.unstarted;
  rate = 1;
  ready = false;
  /** Called when the player reports a new state (e.g. its own play button, or the end). */
  onStateChange: (() => void) | null = null;
  /** Called once, when the player first answers. */
  onReady: (() => void) | null = null;
  private time = 0;
  private timeAt = 0;
  private readonly retry: number;

  constructor(private readonly frame: HTMLIFrameElement) {
    window.addEventListener('message', this.onMessage);
    // The player only reports once it has heard "listening"; repeat until it answers.
    this.retry = window.setInterval(() => {
      if (this.ready) window.clearInterval(this.retry);
      else this.send({ event: 'listening' });
    }, 250);
  }

  dispose(): void {
    window.clearInterval(this.retry);
    window.removeEventListener('message', this.onMessage);
    this.onStateChange = null;
    this.onReady = null;
  }

  /** Video time now, in seconds, smoothed between the player's reports (a few per second). */
  currentTime(): number {
    if (this.state !== PlayerState.playing) return this.time;
    return this.time + ((performance.now() - this.timeAt) / 1000) * this.rate;
  }

  play(): void {
    this.command('playVideo');
  }

  pause(): void {
    this.command('pauseVideo');
  }

  seek(seconds: number): void {
    this.command('seekTo', [seconds, true]);
    this.time = seconds;
    this.timeAt = performance.now();
  }

  setRate(rate: number): void {
    this.command('setPlaybackRate', [rate]);
  }

  /** Volume 0–100. */
  setVolume(volume: number): void {
    this.command('setVolume', [volume]);
  }

  setMuted(muted: boolean): void {
    this.command(muted ? 'mute' : 'unMute');
  }

  private command(func: string, args: unknown[] = []): void {
    this.send({ event: 'command', func, args });
  }

  private send(message: Record<string, unknown>): void {
    this.frame.contentWindow?.postMessage(
      JSON.stringify({ ...message, id: 1, channel: 'widget' }),
      EMBED_ORIGIN,
    );
  }

  private markReady(): void {
    if (this.ready) return;
    this.ready = true;
    this.onReady?.();
  }

  private readonly onMessage = (e: MessageEvent) => {
    if (e.origin !== EMBED_ORIGIN || e.source !== this.frame.contentWindow) return;
    let data: { event?: string; info?: Info | number };
    try {
      data = (typeof e.data === 'string' ? JSON.parse(e.data) : e.data) as typeof data;
    } catch {
      return;
    }
    if (data.event === 'onReady') this.markReady();
    const info = data.info;
    if (data.event !== 'infoDelivery' || typeof info !== 'object') return;
    this.markReady();
    if (info.playbackRate !== undefined) this.rate = info.playbackRate;
    if (info.currentTime !== undefined) {
      this.time = info.currentTime;
      this.timeAt = performance.now();
    }
    if (info.playerState !== undefined && info.playerState !== this.state) {
      if (info.currentTime === undefined) {
        this.time = this.currentTime();
        this.timeAt = performance.now();
      }
      this.state = info.playerState;
      this.onStateChange?.();
    }
  };
}
